import {
  avisarSesionRechazada,
  CABECERA_TOKEN_RENOVADO,
  guardarTokenRenovado,
} from "../utils/sesion";
import { getFromLocalStorage } from "../utils/storage";

/**
 * Lo que cualquier respuesta del API puede decir sobre la sesión.
 *
 * - 401: el token venció o no es válido (solo lo produce JwtMiddleware). Se
 *   avisa para que AuthProvider cierre la sesión y lleve al login, en lugar de
 *   dejar la pantalla abierta con cada consulta fallando.
 * - X-Nuevo-Token: el API renovó el token porque el panel se está usando.
 *
 * `leerCabecera(nombre)` abstrae fetch (headers.get) y XHR (getResponseHeader).
 */
const revisarSesion = (status, leerCabecera) => {
  if (status === 401) {
    avisarSesionRechazada();
    return;
  }

  const renovado = leerCabecera?.(CABECERA_TOKEN_RENOVADO);
  if (renovado) guardarTokenRenovado(renovado);
};

// Tamaño de cada trozo al subir un archivo por partes. 16 MB deja un margen
// amplio frente a los topes del servidor (256 MB de PHP, 512 MB del CDN) y a la
// vez evita hacer cientos de peticiones para un ZIP de varios cientos de MB.
const CHUNK_SIZE = 16 * 1024 * 1024;

// A partir de este tamaño un archivo ya no viaja dentro de la petición final.
const INLINE_LIMIT = 16 * 1024 * 1024;

const esArchivoGrande = (valor, limite) =>
  valor instanceof File && valor.size > limite;

/**
 * Traduce un fallo del servidor a algo que el usuario pueda entender y actuar.
 *
 * Los cuatro casos se ven igual desde el navegador si no se separan aquí: una
 * caída de red, un tope del servidor, un timeout del proxy y un Excel con el
 * formato equivocado terminaban todos como "no fue posible conectar".
 */
export const mensajeDeError = (status, payload) => {
  const detalle = payload?.error;
  const delApi =
    (typeof detalle === "string" && detalle) ||
    detalle?.description ||
    payload?.message ||
    null;

  // 413: el cuerpo excede lo que acepta el servidor.
  if (status === 413) {
    return (
      "El archivo es demasiado grande para el servidor. " +
      "Divide el ZIP en partes más pequeñas e inténtalo de nuevo."
    );
  }

  // 502/503/504: el proxy se cansó de esperar al servidor. No significa que el
  // trabajo se haya perdido, así que conviene revisar antes de repetirlo.
  if (status === 504 || status === 502 || status === 503) {
    return (
      `El servidor tardó demasiado en responder (código ${status}). ` +
      "El proceso puede seguir en marcha: espera un momento y revisa el " +
      "listado antes de repetir la carga."
    );
  }

  if (status === 401) {
    return "Tu sesión expiró. Vuelve a entrar e inténtalo de nuevo.";
  }

  // El resto de los 4xx traen el motivo real del API: formato del Excel,
  // archivo que falta, ZIP ilegible.
  if (delApi) return delApi;

  return `La operación falló (código ${status}).`;
};

// El servidor lo usa como nombre de archivo temporal y solo acepta
// [A-Za-z0-9_-]{8,64}.
const crearUploadId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().replace(/-/g, "");
  }

  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
};

class ApiService {
  constructor(baseURL, bearerToken) {
    this.baseURL = baseURL;

    this.bearerToken = bearerToken;
  }

  // Método para actualizar los headers
  setHeaders(bearerToken) {
    this.bearerToken = bearerToken;
  }

  /**
   * El token más reciente. Se lee al armar cada petición y no solo al crear el
   * servicio: una pantalla abierta mucho rato (una importación larga, por
   * ejemplo) seguía enviando el token con el que se montó aunque el API ya
   * hubiera entregado uno renovado, y a la hora la sesión se cortaba igual.
   */
  tokenActual() {
    return getFromLocalStorage("user")?.jwt || this.bearerToken || "";
  }

  // Método para construir los headers
  buildHeaders() {
    const token = this.tokenActual();

    return {
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
    };
  }

  buildHeadersMultipart() {
    const token = this.tokenActual();

    return {
      Authorization: token ? `Bearer ${token}` : "",
    };
  }

  // Método GET
  async get(url) {
    try {
      const response = await fetch(`${url}`, {
        method: "GET",
        headers: this.buildHeaders(),
      });

      // Antes se devolvía el JSON sin mirar el estado HTTP: el cuerpo de un 401
      // llegaba a las pantallas como si fueran datos válidos y las rompía.
      return await this.handleResponse(response);
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  // Método POST
  async post(url, data) {
    try {
      const response = await fetch(`${url}`, {
        method: "POST",
        headers: this.buildHeaders(),
        body: JSON.stringify(data),
      });

      revisarSesion(response.status, (nombre) => response.headers.get(nombre));

      const result = await response.json();
      return result;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * POST con JSON que sí mira el código de estado.
   *
   * post() devuelve el cuerpo tal cual, así que un 500 llegaba a la pantalla
   * como si fueran datos. Los pasos de la importación necesitan distinguir el
   * error real, y por eso van por aquí.
   */
  async postJson(url, data) {
    let response;

    try {
      response = await fetch(url, {
        method: "POST",
        headers: this.buildHeaders(),
        body: JSON.stringify(data),
      });
    } catch (e) {
      // "Failed to fetch" a secas no dice nada. Pasa cuando el servidor cierra
      // la conexión sin responder, o cuando responde con una página de error
      // generada por encima de PHP (un límite de recursos del hosting): esa
      // respuesta no lleva cabeceras CORS y el navegador no deja ni leerla.
      const error = new Error(
        "El servidor cortó la conexión sin responder. Suele ocurrir cuando " +
          "alcanza un límite de recursos."
      );
      error.status = 0;
      this.handleError(error);
      throw error;
    }

    let payload = null;
    try {
      payload = await response.json();
    } catch (e) {
      payload = null;
    }

    revisarSesion(response.status, (nombre) => response.headers.get(nombre));

    if (response.ok) return payload;

    const message = mensajeDeError(response.status, payload);
    const error = new Error(message);
    error.status = response.status;

    this.handleError(message);
    throw error;
  }

  /**
   * Sube un formulario con archivos, partiendo los que no caben en una sola
   * petición.
   *
   * El CDN corta el cuerpo en 512 MB y PHP en 256 MB (post_max_size). Ese 413
   * lo genera nginx antes de que PHP llegue a ejecutarse, así que la respuesta
   * viene sin las cabeceras CORS del API y el navegador la reporta como un
   * error de CORS o de red, escondiendo el motivo real. Los archivos grandes
   * se suben en trozos y en la petición final solo viaja su identificador.
   */
  async postMultipart(url, formData, onUploadProgress, options = {}) {
    const {
      chunkUrl,
      chunkSize = CHUNK_SIZE,
      inlineLimit = INLINE_LIMIT,
    } = options;

    const entradas = Array.from(formData.entries());
    const hayGrandes = entradas.some(([, valor]) =>
      esArchivoGrande(valor, inlineLimit)
    );

    if (!chunkUrl || !hayGrandes) {
      return this.sendMultipart(url, formData, onUploadProgress);
    }

    const totalBytes = entradas.reduce(
      (suma, [, valor]) => suma + (valor instanceof File ? valor.size : 0),
      0
    );

    // El progreso se lleva por archivo y se suma: si se informara el de cada
    // petición, la barra volvería a cero en cada trozo.
    const subido = {};
    const informar = () => {
      if (typeof onUploadProgress !== "function") return;
      const loaded = Object.values(subido).reduce((a, b) => a + b, 0);
      onUploadProgress({
        loaded,
        total: totalBytes,
        percent: totalBytes
          ? Math.min(100, Math.round((loaded / totalBytes) * 100))
          : null,
      });
    };

    const peticionFinal = new FormData();
    let bytesEnLinea = 0;

    for (const [campo, valor] of entradas) {
      if (!esArchivoGrande(valor, inlineLimit)) {
        if (valor instanceof File) bytesEnLinea += valor.size;
        peticionFinal.append(campo, valor);
        continue;
      }

      const uploadId = await this.uploadInChunks(chunkUrl, valor, {
        chunkSize,
        onProgress: (bytes) => {
          subido[campo] = bytes;
          informar();
        },
      });

      subido[campo] = valor.size;
      informar();

      peticionFinal.append(`${campo}_upload_id`, uploadId);
    }

    return this.sendMultipart(url, peticionFinal, (evento) => {
      subido.__enLinea = Math.min(evento.loaded, bytesEnLinea);
      informar();
    });
  }

  /**
   * Sube un archivo trozo a trozo y devuelve el identificador con el que el
   * servidor lo reensambla. Al ir cada trozo en su propia petición, el tamaño
   * total del archivo deja de estar limitado por el servidor.
   */
  async uploadInChunks(chunkUrl, file, { chunkSize = CHUNK_SIZE, onProgress } = {}) {
    const uploadId = crearUploadId();
    const partes = Math.max(1, Math.ceil(file.size / chunkSize));

    for (let index = 0; index < partes; index += 1) {
      const inicio = index * chunkSize;
      const fin = Math.min(inicio + chunkSize, file.size);

      const formData = new FormData();
      formData.append("uploadId", uploadId);
      formData.append("index", String(index));
      formData.append("total", String(partes));
      formData.append("filename", file.name);
      formData.append("chunk", file.slice(inicio, fin), `${uploadId}-${index}`);

      await this.enviarTrozo(chunkUrl, formData, (evento) => {
        if (onProgress) onProgress(inicio + evento.loaded);
      });

      if (onProgress) onProgress(fin);
    }

    return uploadId;
  }

  /**
   * Reintenta el envío de un trozo ante fallos de red. Una subida de varios
   * cientos de MB son decenas de peticiones y un corte momentáneo no debería
   * obligar a empezar de nuevo. El servidor ignora los trozos repetidos, así
   * que reintentar no duplica bytes.
   */
  async enviarTrozo(chunkUrl, formData, onProgress, intentos = 3) {
    for (let intento = 1; ; intento += 1) {
      try {
        return await this.sendMultipart(chunkUrl, formData, onProgress);
      } catch (error) {
        // Un 4xx es una respuesta del servidor rechazando el trozo, no un fallo
        // de red: repetirlo daría exactamente el mismo error.
        const esDelServidor = error?.status >= 400 && error?.status < 500;
        if (esDelServidor || intento >= intentos) throw error;

        await new Promise((resolve) => setTimeout(resolve, 1000 * intento));
      }
    }
  }

  // Subida con progreso real. fetch() no puede informar el avance de la
  // subida, por eso aquí se usa XMLHttpRequest.
  sendMultipart(url, formData, onUploadProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url);

      // No se fija Content-Type a propósito: el navegador debe generar el
      // boundary del multipart por su cuenta.
      const headers = this.buildHeadersMultipart();
      Object.entries(headers).forEach(([name, value]) => {
        if (value) xhr.setRequestHeader(name, value);
      });

      if (typeof onUploadProgress === "function") {
        xhr.upload.onprogress = (event) => {
          onUploadProgress({
            loaded: event.loaded,
            total: event.total,
            percent: event.lengthComputable
              ? Math.round((event.loaded / event.total) * 100)
              : null,
          });
        };
      }

      xhr.onload = () => {
        revisarSesion(xhr.status, (nombre) => xhr.getResponseHeader(nombre));

        // El cuerpo se interpreta sin dar por hecho que es JSON: cuando el
        // error lo genera el servidor web (un 413, por ejemplo) la respuesta es
        // HTML, y tratar eso como "respuesta inválida" escondía el código real.
        let payload = null;
        try {
          payload = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        } catch (error) {
          payload = null;
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          if (payload === null && xhr.responseText) {
            reject(new Error("La respuesta del servidor no es válida."));
            return;
          }
          resolve(payload);
          return;
        }

        const message = mensajeDeError(xhr.status, payload);

        // El código viaja en el error para que quien reintenta sepa si tiene
        // sentido hacerlo: un 4xx va a fallar igual la segunda vez.
        const error = new Error(message);
        error.status = xhr.status;

        this.handleError(message);
        reject(error);
      };

      // Aquí también cae el 413 del CDN: al venir sin cabeceras CORS, el
      // navegador bloquea la respuesta y no deja leer ni el código de estado.
      xhr.onerror = () => {
        const error = new Error(
          "Se perdió la conexión con el servidor durante la subida. " +
            "Revisa tu conexión e inténtalo de nuevo."
        );
        this.handleError(error);
        reject(error);
      };

      xhr.onabort = () => reject(new Error("Subida cancelada."));

      xhr.send(formData);
    });
  }

  // Método PATCH
  async patch(url, data) {
    try {
      const response = await fetch(`${url}`, {
        method: "PATCH",
        headers: this.buildHeaders(),
        body: JSON.stringify(data),
      });
      return await this.handleResponse(response);
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  // Método PUT
  async put(url, data) {
    try {
      const response = await fetch(`${url}`, {
        method: "put",
        headers: this.buildHeaders(),
        body: JSON.stringify(data),
      });
      return await this.handleResponse(response);
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  // Método DELETE
  async delete(url) {
    try {
      const response = await fetch(`${url}`, {
        method: "DELETE",
        headers: this.buildHeaders(),
      });
      return await this.handleResponse(response);
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  // Método para manejar la respuesta
  async handleResponse(response) {
    revisarSesion(response.status, (nombre) => response.headers.get(nombre));

    if (!response.ok) {
      let payload = null;
      try {
        payload = await response.json();
      } catch (e) {
        // Respuesta sin JSON (por ejemplo una página de error del servidor).
      }

      // El API responde { error: "texto" } en sus validaciones y
      // { error: { type, description } } cuando el error lo genera Slim.
      const detalle = payload?.error;
      const mensaje =
        (typeof detalle === "string" && detalle) ||
        detalle?.description ||
        payload?.message ||
        `La solicitud falló (código ${response.status}).`;

      console.error("Error en la respuesta:", mensaje);
      throw new Error(mensaje);
    }

    return response.json();
  }

  // Método para manejar errores
  handleError(error) {
    console.error("Error en la solicitud:", error);
  }
}

export default ApiService;
